import {
  Component,
  Inject,
  OnInit,
  ViewChild,
  AfterViewInit,
  ChangeDetectorRef,
} from '@angular/core';
import { MODULE_IDS } from 'src/app/constants/module-ids.constant';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialog,
} from '@angular/material/dialog';
import { AddReminderModalComponent } from '../../shared/reminders/components/add-reminder-modal/add-reminder-modal.component';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { BackendApiService } from '../../../services/backend-api.service';
import { ToasterService } from '../../../services/core/toaster.service';
import { formatDate } from '@angular/common';
import { NgDialogAnimationService } from 'src/app/services/core/ng-dialog-animation.service';
import { TaskTemplateDataService } from '../../task/task-template.data.service';
import { TicketUserPopupComponent } from './ticket-user-popup/ticket-user-popup.component';
import { toChecklistItemRefs } from './ticket-checklist-items-popup/ticket-checklist-items-popup.component';
import { CustomerService } from '../../customer/customer.service';
import { VendorService } from '../../inventory/vendor/vendor.service';
import { SubcontractorService } from '../../inventory/subcontractor/subcontractor.service';
import { StaffService } from '../../../services/staff/staff.service';
import { CommunicationServiceService } from '../../widgets/services/communication-service.service';
import { AppConstants } from '../../../constants/app.constants';
import { ScopeDataService } from '../../setting/scope/scope.data.service';
import { HelperService } from '../../../services/helper.service';
import { TicketDataService } from '../ticket.data.service';
import { TicketMainContentComponent } from './ticket-main-content/ticket-main-content.component';
import { TicketSidebarComponent } from './ticket-sidebar/ticket-sidebar.component';
import { TicketTagSelectorPopupComponent } from './ticket-tag-selector-popup/ticket-tag-selector-popup.component';
import { forkJoin, of, Observable, Subject } from 'rxjs';
import { switchMap, catchError, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { UserService } from '../../../services/core/user.service';

/**
 * AddEditTicketComponent
 *
 * Modular ticket creation/edit dialog with sidebar, header, and tab-based content.
 * Matches task component architecture with ticket-specific fields.
 *
 * @selector app-add-edit-ticket
 */
@Component({
    selector: 'app-add-edit-ticket',
    templateUrl: './add-edit-ticket.component.html',
    styleUrls: ['./add-edit-ticket.component.scss'],
    standalone: false
})
export class AddEditTicketComponent implements OnInit, AfterViewInit {
  // Priority and UI state flags
  public CONST_HIGH_PRIORITY: boolean = false;
  public CONST_TIME_PLANNING: boolean = false;
  public CONST_CREATED_BY: boolean = false;
  public CONST_OBSERVER: boolean = false;
  public CONST_PARTICIPANT: boolean = false;

  // Priority state memory for toggle
  private lastNonHighPriority: number = 1; // Default to Low when toggling off from High

  // UI State
  isSidebarCollapsed = false;
  isFullscreen = false;
  isSearchOpen = false;
  searchQuery = '';
  activeTabIndex = 0;
  activeTabKey = '';
  ticketHistory: any[] = [];
  isFavorite = false;
  isBillable = false;

  // Loading States
  isSaving = false;
  isDeleting = false;
  isLoadingData = false;

  // ViewChild references
  @ViewChild(TicketMainContentComponent)
  ticketMainContent!: TicketMainContentComponent;

  @ViewChild(TicketSidebarComponent)
  ticketSidebar!: TicketSidebarComponent;

  // Constants
  public TICKET_MODULE_ID: number = 8;
  /** modules.id for Job — the ticket's job supplies its line items. */
  public JOB_MODULE_ID: number = 19;
  public readonly SELF_ASSIGN_MODULE_ID: number = 11;
  public readonly VENDOR_MODULE_ID: number = 12; // Vendor module ID
  public readonly CUSTOMER_MODULE_ID: number = 13; // Customer module ID
  public readonly SUBCONTRACTOR_MODULE_ID: number = 21; // Subcontractor module ID
  public CONST_RESPONSIBLE_PERSON_TYPE: string = 'responsible_person';
  public CONST_CREATED_BY_TYPE: string = 'created_by';
  public CONST_OBSERVER_TYPE: string = 'observer';
  public CONST_PARTICIPANT_TYPE: string = 'participant';

  // Entity type constants
  TYPE_USER: any = 'Users';
  TYPE_VENDORS: any = 'Vendors';
  TYPE_CUSTOMERS: any = 'Customers';
  TYPE_SUBCONTRACTORS: any = 'Subcontractors';

  // Data properties
  moduleID: number | null;
  referenceID: number | null;
  ticketID: number;
  isTicketModule: boolean = false;
  isEdit: boolean = false;
  model: any = {};
  preSelectedCustomer: any = null; // Pre-selected customer data (passed from Customer Detail page)
  preSelectedVendor: any = null; // Pre-selected vendor data (passed from Vendor Detail page)
  preSelectedSubcontractor: any = null; // Pre-selected subcontractor data (passed from Subcontractor Detail page)
  responsibleUsersArray: any = [];
  createdByArray: any;
  observerArray: any = [];
  participantArray: any = [];
  /** The logged-in user's saved default person per field (GET
   * ticket/default-people — read off ticket_associations.is_default), keyed
   * by 'assignee'/'observer'/'participant'. Only fetched/used for a
   * brand-new ticket, to pre-fill empty People fields. */
  savedDefaults: any = {};
  templates: any = [];
  milestones: any = [];
  statuses: any = [];
  searchParams = {
    type: '',
    module_id: '',
    search: '',
  };
  selectedItem: any = {};
  showSearchInput: boolean = true;
  modulesList: any = [];
  mentions: any = [];
  isMentionsLoading: boolean = false;
  selectedMentionedUsers: any = [];
  private mentionSearchSubject = new Subject<string>();
  private mentionSearchSubscription: any;
  customers: any = [];
  contacts: any = [];
  vendors: any = [];
  ticketTypesList: any = [];
  public scopes: any = [];
  subcontractor: any = [];
  selectedResponsibleParty: any = null;
  attachmentsArr = [];
  attachmentsToAdd = [];
  allowedFileTypes: any = [];
  ticketComments: any[] = [];
  completionPercentage: number = 0;

  // Local tags state for CREATE flow (array of { name, color })
  ticketTags: { id?: number; name: string; color: string }[] = [];

  public lockModuleContext = false;
  public showModuleControls = true;

  public ticketForm: UntypedFormGroup = new UntypedFormGroup({
    title: new UntypedFormControl(null, [Validators.required]),
    description: new UntypedFormControl(null),
    status: new UntypedFormControl(null), // Status ID
    priority: new UntypedFormControl(1), // 1=Low, 2=Medium, 3=High, 4=Urgent
    responsible_person: new UntypedFormControl([]),
    participants: new UntypedFormControl([]),
    observers: new UntypedFormControl([]),
    is_responsible_person: new UntypedFormControl(false),
    is_participant: new UntypedFormControl(false),
    is_observer: new UntypedFormControl(false),
    is_time_planning: new UntypedFormControl(false),
    due_date: new UntypedFormControl(null),
    start_date: new UntypedFormControl(null),
    date_finished: new UntypedFormControl(null),
    is_created_by: new UntypedFormControl(false),
    created_by: new UntypedFormControl(null),
    ticket_type_id: new UntypedFormControl(null),
    scopes: new UntypedFormControl(null),
    duration: new UntypedFormControl(null),
    checklists: new UntypedFormControl(null),
    ticket_items: new UntypedFormControl(null),
    is_template: new UntypedFormControl(false),
    is_billable: new UntypedFormControl(0),
    module_id: new UntypedFormControl(null),
    reference_id: new UntypedFormControl(null),
    milestone_id: new UntypedFormControl(null),
    vendor: new UntypedFormControl(null),
    customer: new UntypedFormControl(null),
    contact: new UntypedFormControl(null),
    subcontractor: new UntypedFormControl(null),
    party_id: new UntypedFormControl(null),
    self_assign: new UntypedFormControl(0),
    mentioned_users: new UntypedFormControl([]),
  });

  constructor(
    public dialogRef: MatDialogRef<AddEditTicketComponent>,
    public backendApiService: BackendApiService,
    public toasterService: ToasterService,
    public dialog: NgDialogAnimationService,
    private matDialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public taskDataService: TaskTemplateDataService,
    public customerService: CustomerService,
    public ticketDataService: TicketDataService,
    public scopeDataService: ScopeDataService,
    public helperService: HelperService,
    public vendorService: VendorService,
    public subcontractorService: SubcontractorService,
    public scrumCommunication: CommunicationServiceService,
    public staffService: StaffService,
    private cdr: ChangeDetectorRef,
    private currentUser: UserService
  ) {
    this.moduleID = this.data?.moduleID ?? null;
    this.referenceID = this.data?.referenceID ?? null;
    this.isTicketModule = this.data?.isTicketModule;
    this.isEdit = this.data?.isEdit;
    this.model = this.data?.model;
    this.ticketID = this.data?.id;
    this.preSelectedCustomer = this.data?.preSelectedCustomer ?? null;
    this.preSelectedVendor = this.data?.preSelectedVendor ?? null;
    this.preSelectedSubcontractor = this.data?.preSelectedSubcontractor ?? null;

    // Fallback: Extract from model if not passed directly (common in edit mode)
    // This ensures proper initialization of context even when callers only pass model
    if (!this.ticketID && this.model?.id) {
      this.ticketID = this.model.id;
    }
    if (!this.moduleID && this.model?.module?.id) {
      this.moduleID = this.model.module.id;
    }
    if (!this.referenceID && this.model?.reference?.id) {
      this.referenceID = this.model.reference.id;
    }

    this.allowedFileTypes = AppConstants.documentFileTypes;

    this.lockModuleContext = this.isModuleContextLocked();
    this.showModuleControls = !this.lockModuleContext;

    // Initialize favorite state in edit when model provided
    if (this.isEdit && this.model && this.model.hasOwnProperty('is_favorite')) {
      this.isFavorite = !!this.model.is_favorite;
    }
  }

  private isModuleContextLocked(): boolean {
    const moduleId = this.moduleID;
    const referenceId = this.referenceID;
    const hasModule = moduleId !== null && moduleId !== undefined;
    const hasReference = referenceId !== null && referenceId !== undefined;
    return hasModule && hasReference;
  }

  /**
   * Pre-select customer and primary contact when ticket is created from Customer Detail page
   * Uses pre-passed customer data (no API call needed - optimized approach)
   * This ensures both customer name and contact name are displayed properly
   */
  private preSelectCustomerAndContact(): void {
    if (!this.preSelectedCustomer) {
      console.warn('[preSelectCustomerAndContact] No customer data available for pre-selection');
      return;
    }

    // Step 1: Call getCustomerContacts to populate contacts array and set form values
    // This method handles setting customer ID and populating contacts dropdown
    this.getCustomerContacts(this.preSelectedCustomer);

    // Step 2: Wait for sidebar to initialize, then trigger its syncFormToState
    // This ensures the sidebar loads customer data for ng-select display
    setTimeout(() => {
      if (this.ticketSidebar) {
        // The sidebar's ngOnChanges will detect the form change and call syncFormToState()
        // which will fetch customer data and populate customerSearchResults$ for display
        this.ticketSidebar.ngOnChanges({
          ticketForm: {
            currentValue: this.ticketForm,
            previousValue: null,
            firstChange: false,
            isFirstChange: () => false
          }
        });
      }
      // Trigger change detection to ensure all components update
      this.cdr.detectChanges();
    }, 150);
  }

  /**
   * Pre-select vendor when ticket is created from Vendor Detail page
   * Uses pre-passed vendor data (no API call needed - optimized approach)
   */
  private preSelectVendor(): void {
    if (!this.preSelectedVendor) {
      console.warn('[preSelectVendor] No vendor data available for pre-selection');
      return;
    }

    // Call setVendor to set vendor ID in form
    this.setVendor(this.preSelectedVendor);

    // Wait for sidebar to initialize, then trigger its syncFormToState
    setTimeout(() => {
      if (this.ticketSidebar) {
        this.ticketSidebar.ngOnChanges({
          ticketForm: {
            currentValue: this.ticketForm,
            previousValue: null,
            firstChange: false,
            isFirstChange: () => false
          }
        });
      }
      this.cdr.detectChanges();
    }, 150);
  }

  /**
   * Pre-select subcontractor when ticket is created from Subcontractor Detail page
   * Uses pre-passed subcontractor data (no API call needed - optimized approach)
   */
  private preSelectSubcontractor(): void {
    if (!this.preSelectedSubcontractor) {
      console.warn('[preSelectSubcontractor] No subcontractor data available for pre-selection');
      return;
    }

    // Call setSubContractor to set subcontractor ID in form
    this.setSubContractor(this.preSelectedSubcontractor);

    // Wait for sidebar to initialize, then trigger its syncFormToState
    setTimeout(() => {
      if (this.ticketSidebar) {
        this.ticketSidebar.ngOnChanges({
          ticketForm: {
            currentValue: this.ticketForm,
            previousValue: null,
            firstChange: false,
            isFirstChange: () => false
          }
        });
      }
      this.cdr.detectChanges();
    }, 150);
  }

  /**
   * Fetch and populate customer when user manually selects Customer module + reference in Ticket module
   * This handles the scenario: User in Ticket module → selects module=Customer → selects reference
   * @param customerId The customer ID from the selected reference
   */
  private fetchAndPopulateCustomerFromReference(customerId: number): void {
    this.backendApiService.getApi(`customers/${customerId}`).subscribe({
      next: (response: any) => {
        const customerData = response?.data;
        if (!customerData) {
          console.warn('[fetchAndPopulateCustomerFromReference] No customer data received');
          return;
        }

        // Call getCustomerContacts to populate contacts array and set form values
        // This method handles setting customer ID and populating contacts dropdown
        this.getCustomerContacts(customerData);

        // CRITICAL: Directly populate sidebar's customerSearchResults$ to avoid duplicate API call
        // We already have customer data, so no need to fetch again via syncFormToState
        if (this.ticketSidebar) {
          this.ticketSidebar.customerSearchResults$ = of([customerData]);
          this.cdr.detectChanges();
        }
      },
      error: (err: any) => {
        console.error('[fetchAndPopulateCustomerFromReference] Failed to fetch customer data:', err);
        // Silently fail - user can still manually select customer
      }
    });
  }

  /**
   * Fetch and populate vendor when user manually selects Vendor module + reference in Ticket module
   * This handles the scenario: User in Ticket module → selects module=Vendor → selects reference
   * @param vendorId The vendor ID from the selected reference
   */
  private fetchAndPopulateVendorFromReference(vendorId: number): void {
    this.backendApiService.getApi(`vendors/${vendorId}`).subscribe({
      next: (response: any) => {
        const vendorData = response?.data;
        if (!vendorData) {
          console.warn('[fetchAndPopulateVendorFromReference] No vendor data received');
          return;
        }

        // Call setVendor to set form values
        this.setVendor(vendorData);

        // CRITICAL: Directly populate sidebar's vendorSearchResults$ to avoid duplicate API call
        // We already have vendor data, so no need to fetch again via syncFormToState
        if (this.ticketSidebar) {
          this.ticketSidebar.vendorSearchResults$ = of([vendorData]);
          this.cdr.detectChanges();
        }
      },
      error: (err: any) => {
        console.error('[fetchAndPopulateVendorFromReference] Failed to fetch vendor data:', err);
        // Silently fail - user can still manually select vendor
      }
    });
  }

  /**
   * Fetch and populate subcontractor when user manually selects Subcontractor module + reference in Ticket module
   * This handles the scenario: User in Ticket module → selects module=Subcontractor → selects reference
   * @param subcontractorId The subcontractor ID from the selected reference
   */
  private fetchAndPopulateSubcontractorFromReference(subcontractorId: number): void {
    this.backendApiService.getApi(`subcontractors/${subcontractorId}`).subscribe({
      next: (response: any) => {
        const subcontractorData = response?.data;
        if (!subcontractorData) {
          console.warn('[fetchAndPopulateSubcontractorFromReference] No subcontractor data received');
          return;
        }

        // Call setSubContractor to set form values
        this.setSubContractor(subcontractorData);

        // CRITICAL: Directly populate sidebar's subcontractorSearchResults$ to avoid duplicate API call
        // We already have subcontractor data, so no need to fetch again via syncFormToState
        if (this.ticketSidebar) {
          this.ticketSidebar.subcontractorSearchResults$ = of([subcontractorData]);
          this.cdr.detectChanges();
        }
      },
      error: (err: any) => {
        console.error('[fetchAndPopulateSubcontractorFromReference] Failed to fetch subcontractor data:', err);
        // Silently fail - user can still manually select subcontractor
      }
    });
  }

  ngOnInit(): void {
    // Ensure self-assign starts OFF in create mode
    this.ticketForm.get('self_assign')?.setValue(0);
    this.ticketForm.get('module_id')?.setValue(this.moduleID);
    this.ticketForm.get('reference_id')?.setValue(this.referenceID);
    this.getModules();
    this.getStatuses();
    // Load milestones if we have both moduleID and referenceID
    if (this.moduleID && this.referenceID) {
      this.loadMilestones();
    }
    // Handle data loading based on mode
    if (this.isEdit) {
      // EDIT MODE: Load ticket types immediately (can load async)
      this.ticketTypes();

      // Load scopes async for ng-select multi-select
      // This ensures ng-select can properly match selected values when form is populated
      this.loadScopeOptions();
    } else {
      // CREATE MODE: Load all dropdowns async (timing doesn't matter)
      this.getAllDropdownData();

      // CRITICAL: Pre-select entity when opened from respective Detail pages
      // Uses pre-passed data (no API call - optimized!)
      if (this.preSelectedCustomer) {
        this.preSelectCustomerAndContact();
      } else if (this.preSelectedVendor) {
        this.preSelectVendor();
      } else if (this.preSelectedSubcontractor) {
        this.preSelectSubcontractor();
      }

      // Pre-select the People fields the moment this dialog opens — not
      // waiting for "+" to be clicked. Editing an existing ticket doesn't
      // need this fetch at all: its own
      // responsible_persons/observers/participants already carry the real,
      // persisted is_default flag straight from ticket_associations.
      this.backendApiService.getApi('ticket/default-people').subscribe({
        next: (response: any) => {
          this.savedDefaults = response?.data || {};
          this.applyDefaultPeopleSelection();
        },
        error: () => this.applyDefaultPeopleSelection(),
      });
    }

    // Add value change subscriptions for date validation
    this.ticketForm.get('start_date')?.valueChanges.subscribe((value) => {
      if (value) {
        this.validateDates();
      }
    });

    this.ticketForm.get('date_finished')?.valueChanges.subscribe((value) => {
      if (value) {
        this.validateDates();
      }
    });

    // Pre-populate mentions array for @mention functionality and edit mode
    this.loadInitialMentions();
    this.setupMentionSearch();
  }

  ngOnDestroy(): void {
    if (this.mentionSearchSubscription) {
      this.mentionSearchSubscription.unsubscribe();
    }
  }

  ngAfterViewInit() {
    if (this.isEdit) {
      this.setDataOnEdit();
    }
  }

  setDataOnEdit() {
    const hasCompleteData =
      this.model?.id &&
      this.model?.name &&
      this.model?.hasOwnProperty('description');

    if (!hasCompleteData && this.ticketID) {
      // Fetch complete ticket data from API
      this.backendApiService.getApi(`ticket/${this.ticketID}`).subscribe({
        next: (response: any) => {
          this.model = response?.data;
          this.populateFormFromModel();
        },
        error: (err: any) => {
          console.error('[setDataOnEdit] Failed to fetch ticket data:', err);
          this.toasterService.showError('Failed to load ticket data');
        },
      });
      return;
    }

    this.populateFormFromModel();
  }

  private populateFormFromModel() {
    // Set mentioned users - align with task module structure (flat array)
    this.selectedMentionedUsers = [];
    this.model?.mentions?.forEach((data: any) => {
      this.selectedMentionedUsers.push({
        id: data?.id,
        full_name: data?.full_name,
      });
    });

    // Set attachments
    const filesData =
      this.model.relations?.attach_files?.map((file: any) => {
        return {
          attach_file: file,
          attachment_id: file.id,
        };
      }) || [];
    this.attachmentsArr = filesData;

    // Set priority flag
    if (this.model?.priority == 3 || this.model?.priority == 4) {
      this.CONST_HIGH_PRIORITY = true;
    }

    // Set billable flag
    this.isBillable = !!this.model?.is_billable;

    // Use patchValue instead of setValue to allow partial updates
    // CRITICAL: Use { emitEvent: false } to prevent valueChanges subscription
    // from overwriting the formatted mentions set by setDescriptionContent()
    this.ticketForm.patchValue(
      {
        title: this.model?.name,
        description: this.model?.description,
        status: this.model?.status || null,
        priority: this.model?.priority || 1,
        responsible_person: [], // Will be populated after mapping arrays below
        participants: [], // Will be populated after mapping arrays below
        observers: [], // Will be populated after mapping arrays below
        is_responsible_person: this.model?.is_responsible_person,
        is_participant: this.model?.is_participant,
        is_observer: this.model?.is_observer,
        is_time_planning: this.model?.is_time_planning,
        due_date: this.model?.due_date
          ? formatDate(this.model.due_date, 'yyyy-MM-dd', 'en-US')
          : null,
        start_date: this.model?.start_date
          ? formatDate(this.model.start_date, 'yyyy-MM-dd', 'en-US')
          : null,
        date_finished: this.model?.date_finished
          ? formatDate(this.model?.date_finished, 'yyyy-MM-dd', 'en-US')
          : null,
        is_created_by: this.model?.is_created_by,
        created_by: this.model?.created_by ? this.model?.created_by?.id : null,
        ticket_type_id: this.model?.ticket_type?.id || null, // Set as ID for mat-select binding
        scopes: this.model?.scopes
          ? this.model.scopes
              .map((s: any) => (typeof s === 'object' && s !== null ? s.id : s))
              .filter((id: any) => id != null)
          : [],
        duration: this.model?.duration,
        checklists: this.model?.check_list,
        ticket_items: this.model?.ticket_items,
        is_template: 0,
        is_billable: this.model?.is_billable ? 1 : 0,
        module_id: this.model?.module?.id,
        reference_id: this.model?.reference?.id,
        milestone_id:
          this.model?.module_data_milestone_id ||
          this.model?.milestone_id ||
          null,
        vendor: this.model?.vendor?.id || null, // Set as ID - sidebar ng-select will handle display
        customer: this.model?.customer?.id || null, // Set as ID - sidebar ng-select will handle display
        subcontractor: this.model?.subcontractor?.id || null, // Set as ID - sidebar ng-select will handle display
        contact: this.model?.contact?.id || null, // Set as ID for mat-select binding
        party_id: this.model?.party?.id || null, // Set responsible party ID
        self_assign: 0,
      },
      { emitEvent: false }
    );

    this.createdByArray = this.model?.created_by;
    this.selectedResponsibleParty = this.model?.party || null;
    console.log('selectedResponsibleParty======?', this.selectedResponsibleParty);

    // Map responsible persons - CREATE NEW ARRAY REFERENCE for OnPush detection
    this.responsibleUsersArray =
      this.model?.responsible_persons?.map((data: any) => ({
        user: this.mapPersonUser(data),
        type: data?.type,
        isDefault: data?.is_default,
      })) || [];

    // Map observers - CREATE NEW ARRAY REFERENCE for OnPush detection
    this.observerArray =
      this.model?.observers?.map((data: any) => ({
        user: this.mapPersonUser(data),
        type: data?.type,
        isDefault: data?.is_default,
      })) || [];

    // Map participants - CREATE NEW ARRAY REFERENCE for OnPush detection
    this.participantArray =
      this.model?.participants?.map((data: any) => ({
        user: this.mapPersonUser(data),
        type: data?.type,
        isDefault: data?.is_default,
      })) || [];

    // CRITICAL: Now sync the populated arrays back to form controls
    // This ensures the sidebar displays the loaded people
    this.ticketForm.patchValue({
      responsible_person: this.responsibleUsersArray,
      participants: this.participantArray,
      observers: this.observerArray,
    });

    // Load tags if available
    if (this.model?.tags && Array.isArray(this.model.tags)) {
      // Map tags and include timestamp fields
      const mappedTags = this.model.tags.map((tag: any) => ({
        id: tag.id ?? tag.tag_id,
        name: tag.name || tag.tag_name || tag.tag || '',
        tag: tag.tag || tag.name || tag.tag_name || '', // Preserve tag field for template fallback
        color: tag.color || tag.tag_color || '#6b7280',
        created_at: tag.created_at,
        updated_at: tag.updated_at,
      }));

      // Sort by created_at descending (last applied tag first)
      this.ticketTags = mappedTags.sort((a: any, b: any) => {
        if (!a.created_at || !b.created_at) return 0;
        return (
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
      });
    }

    // Initialize completion percentage based on current ticket state
    this.completionPercentage = this.calculateCompletionPercentage();

    // Populate description editor with formatted mentions (copied from task module)
    // Use setTimeout to ensure ViewChild is initialized
    setTimeout(() => {
      if (this.ticketMainContent && this.model?.description) {
        this.ticketMainContent.setDescriptionContent(
          this.model.description,
          this.selectedMentionedUsers
        );
      }
    }, 0);
  }

  // =============================================================================
  // DATA LOADING METHODS
  // =============================================================================

  getTaskTemplates() {
    // Tickets don't currently have templates, but keeping for future
    this.templates = [];
  }

  getModules() {
    this.backendApiService
      .getApi('modules/type?type=ticket', {}, false)
      .subscribe((response: any) => {
        this.modulesList = response?.data;
      });
  }

  getStatuses() {
    this.ticketDataService.getStatuses().subscribe({
      next: (response: any) => {
        this.statuses = response?.data || [];
        console.log('Loaded ticket statuses:', this.statuses);

        // Set default status if creating new ticket (not editing)
        if (!this.isEdit && this.statuses.length > 0) {
          const defaultStatus = this.statuses.find(
            (s: any) => s.default_status === true || s.default_status === 1
          );
          if (defaultStatus) {
            this.ticketForm.get('status')?.setValue(defaultStatus.id);
          } else if (this.statuses.length > 0) {
            // Fallback to first status if no default is set
            this.ticketForm.get('status')?.setValue(this.statuses[0].id);
          }
        }
      },
      error: (err: any) => {
        console.error('Failed to load ticket statuses:', err);
        this.statuses = [];
      },
    });
  }

  /**
   * Load milestones for the current module and reference
   * Transforms API response to UI-ready format with calculated fields
   */
  loadMilestones() {
    if (!this.moduleID || !this.referenceID) {
      return;
    }
    this.ticketDataService
      .getMilestones(this.moduleID, this.referenceID)
      .subscribe(
        (response: any) => {
          if (response?.data && Array.isArray(response.data)) {
            // Transform each milestone using transformMilestoneData()
            this.milestones = response.data.map((m: any) =>
              this.transformMilestoneData(m)
            );

            console.log('Loaded and transformed milestones:', this.milestones);

            // If editing and milestone was previously selected, restore selection
            const milestoneId =
              this.model?.module_data_milestone_id || this.model?.milestone_id;

            if (this.isEdit && milestoneId) {
              // Find the milestone in the loaded list
              const selectedMilestone = this.milestones.find(
                (m: any) => m.id === milestoneId
              );

              if (selectedMilestone) {
                // Use setTimeout to ensure ng-select has processed the items
                setTimeout(() => {
                  this.ticketForm.get('milestone_id')?.setValue(milestoneId);
                  this.ticketForm.get('milestone_id')?.updateValueAndValidity();
                }, 100);
              }
            }
          } else {
            this.milestones = [];
          }
        },
        (error) => {
          console.error('Error loading milestones:', error);
          this.milestones = [];
        }
      );
  }

  /**
   * Transform API milestone data to match ticket milestone interface
   *
   * Converts raw milestone data from backend API to UI-ready format with:
   * - Progress calculation based on task completion
   * - Status determination (upcoming/in-progress/completed/overdue)
   * - Days remaining calculation from current date to due date
   * - Proper property mapping from snake_case to camelCase
   *
   * @param apiMilestone - Raw milestone object from API endpoint
   * @returns Transformed milestone object ready for UI display
   */
  private transformMilestoneData(apiMilestone: any): any {
    // ============================================================================
    // TASK STATISTICS CALCULATION
    // ============================================================================

    // Extract tasks array from milestone data
    const tasks = apiMilestone.tasks || [];

    // Count completed tasks using multiple completion indicators
    const completedTasks = tasks.filter(
      (task: any) =>
        task.status === 'completed' ||
        task.status_name?.toLowerCase() === 'completed' ||
        task.completed_at !== null
    );

    // ============================================================================
    // STATUS DETERMINATION
    // ============================================================================

    // Use existing status from API or calculate based on milestone state
    let status = apiMilestone.status;

    // Validate status is one of the expected values
    if (
      !status ||
      !['upcoming', 'in-progress', 'completed', 'overdue'].includes(status)
    ) {
      // Calculate status based on milestone properties
      if (apiMilestone.completed_at) {
        // Milestone marked as complete
        status = 'completed';
      } else if (
        apiMilestone.due_date &&
        new Date(apiMilestone.due_date) < new Date()
      ) {
        // Due date has passed
        status = 'overdue';
      } else if (apiMilestone.progress > 0 || completedTasks.length > 0) {
        // Work has started (progress or completed tasks exist)
        status = 'in-progress';
      } else {
        // No work started yet
        status = 'upcoming';
      }
    }

    // ============================================================================
    // PROGRESS CALCULATION
    // ============================================================================

    // Use API-provided progress or calculate from task completion
    let progress = apiMilestone.progress;

    if (progress === undefined && tasks.length > 0) {
      // Calculate progress as percentage of completed tasks
      progress = Math.round((completedTasks.length / tasks.length) * 100);
    } else if (progress === undefined) {
      // No tasks and no progress - default to 0
      progress = 0;
    }

    // ============================================================================
    // DAYS REMAINING CALCULATION
    // ============================================================================

    // Use API-provided days_left or calculate from due_date
    let daysRemaining = apiMilestone.days_left;

    if (daysRemaining === undefined && apiMilestone.due_date) {
      // Calculate days from today to due date
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to midnight for accurate day count

      const dueDate = new Date(apiMilestone.due_date);
      dueDate.setHours(0, 0, 0, 0); // Reset time to midnight

      // Calculate difference in milliseconds
      const diffTime = dueDate.getTime() - today.getTime();

      // Convert milliseconds to days (ceil to round up partial days)
      daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }

    // ============================================================================
    // PROPERTY MAPPING & RETURN
    // ============================================================================

    // Build transformed milestone object with UI-friendly property names
    const transformed = {
      id: apiMilestone.id,

      // Name - handle multiple possible property names from API
      name:
        apiMilestone.milestone_name || apiMilestone.name || 'Unnamed Milestone',

      // Dates - support both snake_case and camelCase from API
      dueDate: apiMilestone.due_date || apiMilestone.dueDate,
      startDate: apiMilestone.start_date || apiMilestone.startDate,
      endDate: apiMilestone.end_date || apiMilestone.endDate,

      // Calculated/validated values
      status: status,
      progress: progress,
      daysRemaining: daysRemaining,

      // Task statistics - only include if tasks exist
      taskStats:
        tasks.length > 0
          ? {
              total: tasks.length,
              completed: completedTasks.length,
            }
          : undefined,

      // Additional metadata
      description: apiMilestone.description,
      assignedTo: apiMilestone.assigned_to_name || apiMilestone.assignedTo,
    };

    return transformed;
  }

  getAllDropdownData() {
    this.scopesList();
    this.ticketTypes();
  }

  ticketTypes() {
    this.backendApiService
      .getApi('ticket-types', {}, false)
      .subscribe((response: any) => {
        this.ticketTypesList = response;
      });
  }

  scopesList() {
    this.loadScopeOptions();
  }

  /**
   * Options for the sidebar Scopes selector.
   *
   * On a Job ticket these are the scopes that actually exist on that job — the
   * same source the Materials picker's "Link to scope" uses. Every other module
   * keeps the office-wide catalogue, since it has no job to narrow by.
   *
   * The DISPLAY comes from job_scopes, but the stored value stays the master
   * `scope_id`, because the ticket's scopes are saved to the scope_ticket pivot
   * which holds master scope ids on every module.
   */
  /**
   * Identifies which source the scope options came from, so a reload only fires
   * when the answer would actually differ — switching between two customers, say,
   * must not re-fetch the same office catalogue.
   */
  private lastScopeSourceKey: string | null = null;

  /** Re-run the scope load if the module/reference change moved the source. */
  private reloadScopeOptionsIfNeeded(): void {
    const key = this.isJobTicket ? `job:${this.getTicketJobId()}` : 'office';

    if (key === this.lastScopeSourceKey) {
      return;
    }
    this.loadScopeOptions();
  }

  private loadScopeOptions(): void {
    // Stamped before the request so two rapid changes cannot both fire.
    this.lastScopeSourceKey = this.isJobTicket ? `job:${this.getTicketJobId()}` : 'office';

    if (!this.isJobTicket) {
      this.scopeDataService.userSelectedScopesList().subscribe({
        next: (response: any) => (this.scopes = response.data || []),
        error: () => (this.scopes = []),
      });
      return;
    }

    this.ticketDataService.getJobScopes(this.getTicketJobId()).subscribe({
      next: (response: any) => {
        const rows = response?.data?.data ?? response?.data ?? [];
        this.scopes = this.withAlreadySelectedScopes(this.toScopeOptions(rows));
      },
      error: () => (this.scopes = []),
    });
  }

  /**
   * job_scopes rows → { id, name } for ng-select, keyed on the MASTER scope id.
   *
   * Two job scopes can point at the same master scope, so de-duplicate — an
   * ng-select with repeated ids selects both rows at once.
   */
  private toScopeOptions(rows: any[]): any[] {
    const seen = new Set<number>();

    return (Array.isArray(rows) ? rows : [])
      .filter((row: any) => {
        const id = row?.scope_id;
        if (!id || seen.has(id)) {
          return false;
        }
        seen.add(id);
        return true;
      })
      .map((row: any) => ({ id: row.scope_id, name: row.scope_name }));
  }

  /**
   * Keep scopes the ticket already carries that are no longer on the job.
   *
   * ng-select renders a selection only if its id is among the items, so without
   * this an existing scope would vanish from the field on edit — and be silently
   * dropped on save, since the control is rebuilt from what is displayed.
   */
  private withAlreadySelectedScopes(options: any[]): any[] {
    const selected = Array.isArray(this.model?.scopes) ? this.model.scopes : [];
    const known = new Set(options.map((option: any) => option.id));

    const missing = selected
      .filter((scope: any) => scope?.id && !known.has(scope.id))
      .map((scope: any) => ({ id: scope.id, name: scope.name }));

    return [...options, ...missing];
  }

  /** True when this ticket belongs to a Job — gates job-specific behaviour. */
  get isJobTicket(): boolean {
    return this.moduleID === this.JOB_MODULE_ID && !!this.getTicketJobId();
  }

  // =============================================================================
  // FIELD CHANGE HANDLERS
  // =============================================================================

  handleFieldChange(event: any) {
    const { field, value } = event;
    this.ticketForm.get(field)?.setValue(value);
  }

  handleStatusChange(status: any) {
    // Now receives full status object from sidebar
    if (status?.id) {
      this.ticketForm.get('status')?.setValue(status.id);
    } else if (typeof status === 'number' || typeof status === 'string') {
      // Fallback: if just ID is passed (backward compatibility)
      this.ticketForm.get('status')?.setValue(status);
    }

    // Recalculate progress when status changes (might be marked complete)
    this.completionPercentage = this.calculateCompletionPercentage();
  }

  selectedModule(event: any) {
    if (event) {
      this.searchParams.type = event?.slug;
      this.searchParams.module_id = event?.id;
      this.searchParams.search = '';
      this.moduleID = event?.id || null;
      this.referenceID = null;
      this.ticketForm.get('module_id')?.setValue(event?.id);
      // The scope list means something different per module (a job's own scopes
      // vs the office catalogue), so previously chosen scopes no longer apply.
      this.resetScopeSelection();
      // Load milestones when module selected
      if (this.ticketForm.get('reference_id')?.value) {
        this.loadMilestones();
      }
    } else {
      this.ticketForm.get('module_id')?.setValue('');
      this.moduleID = null;
      this.resetScopeSelection();
    }
    this.ticketForm.get('reference_id')?.setValue('');
    this.referenceID = null;
    this.showSearchInput = true;
    this.selectedItem = '';
    this.reloadScopeOptionsIfNeeded();
  }

  /**
   * Clear chosen scopes. ng-select cannot render a selection whose id is absent
   * from its items, so a stale id would disappear from the field and then be
   * dropped on save — clearing makes that explicit instead of silent.
   */
  private resetScopeSelection(): void {
    this.ticketForm.get('scopes')?.setValue([]);
  }

  onItemSelect(event: any) {
    this.selectedItem = event;
    this.ticketForm.get('reference_id')?.setValue(event?.id);
    this.referenceID = event?.id || null;
    this.showSearchInput = false;

    // Load milestones when reference selected
    if (this.ticketForm.get('module_id')?.value) {
      this.moduleID = this.ticketForm.get('module_id')?.value;
      this.loadMilestones();
    }

    // Picking the job is what makes the job's scopes available, so the sidebar
    // options are re-sourced here as well as on module change.
    this.reloadScopeOptionsIfNeeded();

    // CRITICAL: Auto-populate entity data when user selects module + reference
    // Only applies when user is in Ticket module and manually selects module + reference (not in edit mode)
    const currentModuleId = this.ticketForm.get('module_id')?.value;
    if (event?.id && !this.isEdit) {
      if (currentModuleId === this.CUSTOMER_MODULE_ID) {
        // Auto-populate customer & contact when Customer module reference selected
        this.fetchAndPopulateCustomerFromReference(event.id);
      } else if (currentModuleId === this.VENDOR_MODULE_ID) {
        // Auto-populate vendor when Vendor module reference selected
        this.fetchAndPopulateVendorFromReference(event.id);
      } else if (currentModuleId === this.SUBCONTRACTOR_MODULE_ID) {
        // Auto-populate subcontractor when Subcontractor module reference selected
        this.fetchAndPopulateSubcontractorFromReference(event.id);
      }
    }
  }

  updateSearchInputFlag() {
    this.showSearchInput = true;
    this.ticketForm.get('reference_id')?.setValue(null);
    this.referenceID = null;
  }

  handleSelfAssignChange(value: boolean) {
    this.ticketForm.get('self_assign')?.setValue(value ? 1 : 0);

    if (value) {
      const loginUserId = this.currentUser?.getID?.();
      this.moduleID = this.SELF_ASSIGN_MODULE_ID;
      this.referenceID = loginUserId ? Number(loginUserId) : null;
      this.ticketForm.get('module_id')?.setValue(this.moduleID);
      this.ticketForm.get('reference_id')?.setValue(this.referenceID);
      this.milestones = [];
      this.showSearchInput = false;
      this.selectedItem = '';
    } else {
      this.moduleID = null;
      this.referenceID = null;
      this.ticketForm.get('module_id')?.setValue(null);
      this.ticketForm.get('reference_id')?.setValue(null);
      this.showSearchInput = true;
      this.selectedItem = '';
    }
  }

  handleMilestoneSelect(milestone: any) {
    this.ticketForm.get('milestone_id')?.setValue(milestone?.id);
  }

  handleMilestoneRemove() {
    this.ticketForm.get('milestone_id')?.setValue(null);
  }

  toggleBillable() {
    this.isBillable = !this.isBillable;
    this.ticketForm
      .get('is_billable')
      ?.setValue(this.isBillable ? 1 : 0, { emitEvent: false });
  }

  changePriority(event: any = '') {
    this.CONST_HIGH_PRIORITY = !this.CONST_HIGH_PRIORITY;
    // Toggle between High and the last non-high priority
    if (this.CONST_HIGH_PRIORITY) {
      this.lastNonHighPriority = this.ticketForm.get('priority')?.value || 1;
      this.ticketForm.get('priority')?.setValue(3); // High
    } else {
      this.ticketForm.get('priority')?.setValue(this.lastNonHighPriority);
    }
  }

  hideShowTimePlanning() {
    this.CONST_TIME_PLANNING = !this.CONST_TIME_PLANNING;
    this.ticketForm.get('is_time_planning')?.setValue(this.CONST_TIME_PLANNING);
  }

  hideShowCreatedBy() {
    this.CONST_CREATED_BY = !this.CONST_CREATED_BY;
  }

  hideShowObserver() {
    this.CONST_OBSERVER = !this.CONST_OBSERVER;
  }

  hideShowParticipant() {
    this.CONST_PARTICIPANT = !this.CONST_PARTICIPANT;
  }

  // =============================================================================
  // PEOPLE MANAGEMENT (Assignees, Observers, Participants)
  // =============================================================================

  openUsersPopup(
    event: any,
    type: any,
    attachAbleUser: any = ['Users', 'Vendors', 'Customers', 'Subcontractors']
  ) {
    const dialogRef = this.dialog.open(TicketUserPopupComponent, {
      width: '420px',
      height: '500px',
      data: {
        attachAbleUser,
        type: type,
      },
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        if (result.type == this.CONST_RESPONSIBLE_PERSON_TYPE) {
          this.responsibleUsersArray = this.addUser(this.responsibleUsersArray, {
            user: result.user,
            type: result?.selectedTab,
          });
        } else if (result.type == this.CONST_CREATED_BY_TYPE) {
          this.ticketForm.get('created_by')?.setValue(result.user?.id);
          this.createdByArray = result.user;
        } else if (result.type == this.CONST_OBSERVER_TYPE) {
          this.observerArray = this.addUser(this.observerArray, {
            user: result.user,
            type: result?.selectedTab,
          });
        } else if (result.type == this.CONST_PARTICIPANT_TYPE) {
          this.participantArray = this.addUser(this.participantArray, {
            user: result.user,
            type: result?.selectedTab,
          });
        }
      }
    });
  }

  addUser(usersList: any[], userObj: any): any[] {
    const alreadyExistCheck = usersList.find(
      (x: any) => x.user.id == userObj.user.id && x.type == userObj.type
    );
    if (!alreadyExistCheck) {
      return [...usersList, userObj]; // New array reference so OnPush children (ticket-sidebar) detect the change
    }
    return usersList;
  }

  handleAssigneeSelected(assignee: any) {
    this.responsibleUsersArray = this.addUser(this.responsibleUsersArray, assignee);
  }

  removeAssignee(index: number) {
    this.responsibleUsersArray = [
      ...this.responsibleUsersArray.slice(0, index),
      ...this.responsibleUsersArray.slice(index + 1),
    ];
  }

  handleObserverSelected(observer: any) {
    this.observerArray = this.addUser(this.observerArray, observer);
  }

  removeObserver(index: number) {
    this.observerArray = [
      ...this.observerArray.slice(0, index),
      ...this.observerArray.slice(index + 1),
    ];
  }

  handleParticipantSelected(participant: any) {
    this.participantArray = this.addUser(this.participantArray, participant);
  }

  removeParticipant(index: number) {
    this.participantArray = [
      ...this.participantArray.slice(0, index),
      ...this.participantArray.slice(index + 1),
    ];
  }

  /**
   * User clicked a different row's star in the picker dropdown to make THAT
   * one the default instead of whichever was positionally first. Swaps them
   * into the field: removes whoever is currently flagged isDefault (if the
   * user hadn't already removed them by hand), then adds the newly-picked
   * one in their place. `user` already arrives wrapped as {user, type,
   * isDefault} from ticket-sidebar.component.ts's setDefaultAssignee() etc.
   */
  /**
   * Fires both when the dropdown's star picks someone not yet selected, and
   * when the star next to an ALREADY-selected person is clicked to promote
   * them. In the latter case the person is already in the array, so
   * remove-then-readd would silently no-op (addUser's own dedupe check
   * would just hand back the untouched array, dropping the isDefault flag)
   * — remap in place instead whenever that's the case. Nothing is
   * persisted here directly — whichever person ends up flagged isDefault
   * is included as default_*_id the next time getFormData()/save() runs,
   * and stored as a flag on that person's own ticket_associations row.
   */
  handleDefaultAssigneeChanged(user: any): void {
    const alreadySelectedIndex = this.responsibleUsersArray.findIndex(
      (x: any) => x.user.id == user.user.id && x.type == user.type
    );
    if (alreadySelectedIndex > -1) {
      this.responsibleUsersArray = this.responsibleUsersArray.map((x: any) => ({
        ...x,
        isDefault: x.user.id == user.user.id && x.type == user.type,
      }));
    } else {
      const index = this.responsibleUsersArray.findIndex((x: any) => x.isDefault);
      if (index > -1) {
        this.removeAssignee(index);
      }
      this.handleAssigneeSelected(user);
    }
  }

  handleDefaultObserverChanged(user: any): void {
    const alreadySelectedIndex = this.observerArray.findIndex(
      (x: any) => x.user.id == user.user.id && x.type == user.type
    );
    if (alreadySelectedIndex > -1) {
      this.observerArray = this.observerArray.map((x: any) => ({
        ...x,
        isDefault: x.user.id == user.user.id && x.type == user.type,
      }));
    } else {
      const index = this.observerArray.findIndex((x: any) => x.isDefault);
      if (index > -1) {
        this.removeObserver(index);
      }
      this.handleObserverSelected(user);
    }
  }

  handleDefaultParticipantChanged(user: any): void {
    const alreadySelectedIndex = this.participantArray.findIndex(
      (x: any) => x.user.id == user.user.id && x.type == user.type
    );
    if (alreadySelectedIndex > -1) {
      this.participantArray = this.participantArray.map((x: any) => ({
        ...x,
        isDefault: x.user.id == user.user.id && x.type == user.type,
      }));
    } else {
      const index = this.participantArray.findIndex((x: any) => x.isDefault);
      if (index > -1) {
        this.removeParticipant(index);
      }
      this.handleParticipantSelected(user);
    }
  }

  /**
   * Pre-selects Assignee/Observer/Participant with each field's saved
   * default (this.savedDefaults, from GET ticket/default-people, fetched
   * just before this is called) when the user has one. Falls back to the
   * picker's own "first result" for any field that doesn't have a saved
   * preference yet (first time ever using the picker). Wraps the chosen
   * staff member the same {user, type} shape onAssigneeSelect() itself
   * builds — required by addUser()'s dedupe check (x.user.id).
   */
  private applyDefaultPeopleSelection(): void {
    const needsFallback =
      (!this.savedDefaults.assignee && this.responsibleUsersArray.length === 0) ||
      (!this.savedDefaults.observer && this.observerArray.length === 0) ||
      (!this.savedDefaults.participant && this.participantArray.length === 0);

    if (this.savedDefaults.assignee && this.responsibleUsersArray.length === 0) {
      this.handleAssigneeSelected({ user: this.savedDefaults.assignee, type: 'Users', isDefault: true });
    }
    if (this.savedDefaults.observer && this.observerArray.length === 0) {
      this.handleObserverSelected({ user: this.savedDefaults.observer, type: 'Users', isDefault: true });
    }
    if (this.savedDefaults.participant && this.participantArray.length === 0) {
      this.handleParticipantSelected({ user: this.savedDefaults.participant, type: 'Users', isDefault: true });
    }

    if (!needsFallback) {
      return;
    }

    this.backendApiService
      .getApi('staff/search', { search: '', page: 1, per_page: 1 })
      .subscribe({
        next: (response: any) => {
          const fallbackUser = this.staffService.processStaff(response?.data || [])[0];
          if (!fallbackUser) {
            return;
          }
          const wrapped = { user: fallbackUser, type: 'Users', isDefault: true };
          if (!this.savedDefaults.assignee && this.responsibleUsersArray.length === 0) {
            this.handleAssigneeSelected(wrapped);
          }
          if (!this.savedDefaults.observer && this.observerArray.length === 0) {
            this.handleObserverSelected(wrapped);
          }
          if (!this.savedDefaults.participant && this.participantArray.length === 0) {
            this.handleParticipantSelected(wrapped);
          }
        },
      });
  }

  remove(index: any, dataArray: any) {
    dataArray.splice(index, 1);
  }

  // =============================================================================
  // TAGS MANAGEMENT
  // =============================================================================

  openTagSelector(): void {
    const dialogRef = this.matDialog.open(TicketTagSelectorPopupComponent, {
      width: '480px',
      maxWidth: '480px',
      maxHeight: '80vh',
      panelClass: 'ticket-tag-selector-popup-dialog-container',
      data: { currentTags: this.ticketTags },
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (!result || !result.tag) return;
      const selected = result.tag;
      const name: string = (selected?.name || selected?.tag || '')
        .toString()
        .trim();
      const color: string = selected?.color || 'gray';
      if (!name) return;

      // Prevent duplicates (case-insensitive)
      const exists = this.ticketTags.some(
        (t) => t.name.toLowerCase() === name.toLowerCase()
      );
      if (exists) {
        this.toasterService.showInfo('Tag already added');
        return;
      }

      // Both CREATE and EDIT mode: Update local array only
      // Tags will be persisted when user clicks "Save Ticket"
      this.ticketTags = [...this.ticketTags, { name, color }];
    });
  }

  removeTagByIndex(index: number): void {
    // Bounds checking to prevent errors
    if (index < 0 || index >= this.ticketTags.length) return;

    // Use immutable pattern to trigger change detection
    this.ticketTags = [
      ...this.ticketTags.slice(0, index),
      ...this.ticketTags.slice(index + 1),
    ];

    // Trigger change detection
    this.cdr.detectChanges();
  }

  handleTagsUpdated(
    tags: { id?: number; name: string; color: string }[]
  ): void {
    if (!Array.isArray(tags)) return;
    this.ticketTags = tags.map((tag) => ({
      name: tag?.name || '',
      color: tag?.color || 'gray',
      ...(tag?.id ? { id: tag.id } : {}),
    }));
  }

  // =============================================================================
  // DATE HANDLERS
  // =============================================================================

  handleStartDateChange(date: Date) {
    if (date) {
      // Format as YYYY-MM-DD for date input binding
      const formattedDate = formatDate(date, 'yyyy-MM-dd', 'en_US');
      this.ticketForm.get('start_date')?.setValue(formattedDate);
      this.validateDates();
    }
  }

  handleDurationChange(duration: number) {
    this.ticketForm.get('duration')?.setValue(duration);
  }

  handleFinishDateChange(date: Date) {
    if (date) {
      // Format as YYYY-MM-DD for date input binding
      const formattedDate = formatDate(date, 'yyyy-MM-dd', 'en_US');
      this.ticketForm.get('date_finished')?.setValue(formattedDate);
      this.validateDates();
    }
  }

  validateDates() {
    const startDate = this.ticketForm.get('start_date')?.value;
    const finishDate = this.ticketForm.get('date_finished')?.value;

    if (startDate && finishDate) {
      const start = new Date(startDate);
      const finish = new Date(finishDate);

      if (start > finish) {
        this.ticketForm.get('start_date')?.setErrors({ dateError: true });
        return false;
      } else {
        this.ticketForm.get('start_date')?.setErrors(null);
        return true;
      }
    }
    return true;
  }

  // =============================================================================
  // MENTIONS & COMMENTS
  // =============================================================================

  loadInitialMentions(): void {
    this.isMentionsLoading = true;
    this.backendApiService
      .getApi('staff/search?search=', [], false)
      .subscribe({
        next: (response: any) => {
          this.mentions = this.staffService.processStaff(response.data || []);
          this.isMentionsLoading = false;
          console.log('[MENTIONS] Initial load:', this.mentions.length, 'users');
        },
        error: (err: any) => {
          console.error('[MENTIONS] Failed to load initial users:', err);
          this.mentions = [];
          this.isMentionsLoading = false;
        },
      });
  }

  setupMentionSearch(): void {
    this.mentionSearchSubscription = this.mentionSearchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe((searchQuery: string) => {
        this.performMentionSearch(searchQuery);
      });
  }

  performMentionSearch(searchQuery: string): void {
    this.isMentionsLoading = true;
    this.backendApiService
      .getApi(`staff/search?search=${searchQuery}`, [], false)
      .subscribe({
        next: (response: any) => {
          this.mentions = this.staffService.processStaff(response.data || []);
          this.isMentionsLoading = false;
          console.log('[MENTIONS] Search results for "' + searchQuery + '":', this.mentions.length, 'users');
        },
        error: (err: any) => {
          console.error('[MENTIONS] Search failed:', err);
          this.mentions = [];
          this.isMentionsLoading = false;
        },
      });
  }

  getUsers(event: any) {
    const search = typeof event === 'string' ? event : event?.text || '';
    this.mentionSearchSubject.next(search);
  }

  getSelectedUser(event: any) {
    // Handle both single user and array (from custom component)
    if (Array.isArray(event)) {
      // Array of mentioned users from child component
      this.selectedMentionedUsers = event;
    } else {
      // Single user selection
      const index = this.selectedMentionedUsers.findIndex(
        (x: any) => x.id == event?.id
      );
      if (index == -1) {
        this.selectedMentionedUsers.push(event);
      }
    }
  }

  handleCommentAdded() {
    // Refresh comments count
    this.ticketComments.push({});
  }

  // =============================================================================
  // ATTACHMENTS & CHECKLISTS
  // =============================================================================

  /**
   * Handle attachment upload in CREATE/EDIT mode
   * Collects attachment IDs from uploaded files for direct upload (create mode)
   * or folder-based upload (edit mode with ticket ID)
   */
  getFileID(event: any) {
    const data = event?.attachment;
    if (Array.isArray(data)) {
      this.attachmentsArr = data as any;
      // Extract attachment_ids from each file
      this.attachmentsToAdd = this.attachmentsArr.map(
        (file: any) => file.attachment_id || file.attach_file?.id
      ) as any;
      console.log('Updated attachment IDs:', this.attachmentsToAdd);
    }
  }

  /**
   * Handle attachment deletion in CREATE mode
   * The app-attachment component handles server-side deletion
   * and emits callback with remaining files automatically
   */
  handleAttachmentDelete(event: any): void {
    console.log('Attachment deleted, attachment_ids will be updated on next callback');
  }

  getChecklists(event: any) {
    this.ticketForm.get('checklists')?.setValue(event);

    // Recalculate progress whenever checklists change
    this.completionPercentage = this.calculateCompletionPercentage();
  }

  getChecklistCount(): number {
    const checklists = this.ticketForm.get('checklists')?.value;
    return Array.isArray(checklists) ? checklists.length : 0;
  }

  getMaterials(event: any) {
    this.ticketForm.get('ticket_items')?.setValue(event);
  }

  /**
   * Materials & Services applies only to tickets on a Job — other modules have
   * no scope items to draw from, so the tab is hidden entirely.
   */
  get showMaterialsTab(): boolean {
    return this.isJobTicket;
  }

  getMaterialsCount(): number {
    const items = this.ticketForm.get('ticket_items')?.value;
    return Array.isArray(items) ? items.length : 0;
  }

  /**
   * The job this ticket belongs to, for the Materials & Services item lookup.
   * In edit mode it comes off the loaded ticket; in create mode the dialog is
   * opened from a module record, so a Job reference is the job itself.
   */
  getTicketJobId(): number | null {
    if (this.model?.job_id) {
      return this.model.job_id;
    }
    const referenceType = this.model?.reference_type || '';
    if (referenceType.endsWith('Job') && this.model?.reference?.id) {
      return this.model.reference.id;
    }
    return this.moduleID === this.JOB_MODULE_ID ? this.referenceID : null;
  }

  getAttachmentsCount(): number {
    return this.attachmentsArr.length;
  }

  // =============================================================================
  // TICKET-SPECIFIC HANDLERS
  // =============================================================================

  getCustomerContacts(event: any) {
    if (event) {
      this.contacts = event?.contacts || [];

      // ALWAYS auto-select contact in create mode:
      // Priority 1: Primary contact (is_primary === 1)
      // Priority 2: First contact in array
      if (!this.isEdit) {
        const primaryContact = this.contacts.find((c: any) => c.is_primary === 1);
        const contactToSelect = primaryContact || this.contacts[0];

        if (contactToSelect) {
          this.ticketForm.get('contact')?.setValue(contactToSelect.id);
        }
      }

      // FIX: Extract ID before setting, don't set full object (breaks save)
      this.ticketForm
        .get('customer')
        ?.setValue(typeof event === 'object' ? event?.id : event);
    } else {
      this.contacts = [];
      this.ticketForm.get('contact')?.setValue(null);
      this.ticketForm.get('customer')?.setValue(null);
    }
  }

  setVendor(event: any) {
    // FIX: Extract ID before setting, don't set full object (breaks save)
    this.ticketForm
      .get('vendor')
      ?.setValue(typeof event === 'object' ? event?.id : event);
  }

  setSubContractor(event: any) {
    // FIX: Extract ID before setting, don't set full object (breaks save)
    this.ticketForm
      .get('subcontractor')
      ?.setValue(typeof event === 'object' ? event?.id : event);
  }

  setResponsibleParty(event: any) {
    // Store full object for display
    this.selectedResponsibleParty = event;
    // Set ID for form/save
    this.ticketForm
      .get('party_id')
      ?.setValue(typeof event === 'object' ? event?.id : event);
  }

  // =============================================================================
  // UI STATE HANDLERS
  // =============================================================================

  onSidebarCollapse(collapsed: boolean) {
    this.isSidebarCollapsed = collapsed;
  }

  handleSearch(event: any) {
    this.isSearchOpen = event.isOpen;
    this.searchQuery = event.query;
  }

  onHeaderSearchToggle(isOpen: boolean) {
    this.isSearchOpen = isOpen;
  }

  onHeaderSearchChange(query: string) {
    this.searchQuery = query;
  }

  onFullscreenToggle() {
    this.isFullscreen = !this.isFullscreen;
  }

  handleTabChange(tabIndex: number) {
    this.activeTabIndex = tabIndex;
  }

  /** Which tab is open, by stable key — indices shift as tabs are hidden. */
  handleTabKeyChange(tabKey: string) {
    this.activeTabKey = tabKey;
  }

  handleEditDescription() {
    // Focus description field
  }

  toggleFavorite() {
    // Prevent favoriting unsaved tickets
    if (!this.ticketID) {
      this.toasterService.showWarning('Please save the ticket first');
      return;
    }

    // Store previous state for rollback on error
    const previousState = this.isFavorite;

    // Optimistic UI update
    this.isFavorite = !this.isFavorite;

    // Prepare backend payload
    const payload = {
      reference_id: this.ticketID,
      module_id: 8, // Ticket module ID
      is_favorite: this.isFavorite ? 1 : 0,
    };

    // Call backend API
    this.backendApiService.postApi('user/favorites/mark', payload).subscribe(
      (response: any) => {
        // Success - show toast notification
        this.toasterService.showSuccessMessage(
          this.isFavorite
            ? 'Ticket added to favorites'
            : 'Ticket removed from favorites'
        );

        // Update model if it exists
        if (this.model) {
          this.model.is_favorite = this.isFavorite ? 1 : 0;
        }
      },
      (error: any) => {
        // Error - rollback optimistic update
        this.isFavorite = previousState;

        // Show error toast
        this.toasterService.showError(
          error?.error?.message || 'Failed to update favorite state'
        );
      }
    );
  }

  setReminder() {
    // Only allow if ticket is saved (has ID)
    if (!this.ticketID) {
      this.toasterService.showWarning('Please save the ticket first before setting a reminder');
      return;
    }

    const ticketName = this.model?.name || this.model?.title || `Ticket #${this.ticketID}`;
    const dialogData: any = {
      reminderable_type: 'App\\Models\\Ticket',
      reminderable_id: this.ticketID,
      entityName: ticketName
    };

    // Add location if present in ticket model
    if (this.model?.location) {
      dialogData.location = this.model.location;
    }

    const dialogRef = this.matDialog.open(AddReminderModalComponent, {
      panelClass: 'reminder-modal-panel',
      disableClose: false,
      autoFocus: true,
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.toasterService.showSuccessMessage('Reminder set successfully!');
      }
    });
  }

  // =============================================================================
  // GETTERS FOR TEMPLATE
  // =============================================================================

  getProjectName(): string {
    return this.selectedItem?.data || this.model?.reference?.name || '';
  }

  getModuleLabel(): string {
    const module = this.modulesList.find((m: any) => m.id === this.moduleID);
    return module?.name || 'Ticket';
  }

  /**
   * Linked record's id, shown as "#id" next to the module/reference name.
   * Was returning this.model.id (the ticket's own id) instead of the
   * referenced record's — referenceID already tracks the right id across
   * both edit (loaded from model.reference.id) and create (from the
   * search-selected item) modes.
   */
  getRecordNumber(): string {
    return this.referenceID?.toString() || '';
  }

  /**
   * Detail-page routes for module ids a ticket's linked record can point to.
   * Mirrors view-ticket-detail.component.ts's LINKED_RECORD_ROUTES.
   */
  private readonly LINKED_RECORD_ROUTES: Partial<Record<number, (id: number | string) => string>> = {
    [MODULE_IDS.PRODUCT]: (id) => `/products/product/${id}`,
    [MODULE_IDS.STAFF]: (id) => `/hr/staff/${id}`,
    [MODULE_IDS.VENDOR]: (id) => `/vendors/vendor/${id}`,
    [MODULE_IDS.CUSTOMER]: (id) => `/customers/customer/${id}`,
    [MODULE_IDS.BID]: (id) => `/bids/bid/${id}`,
    [MODULE_IDS.WAREHOUSE]: (id) => `/warehouses/warehouse/${id}`,
    [MODULE_IDS.JOB]: (id) => `/jobs/detail/${id}`,
    [MODULE_IDS.CHANGE_ORDER]: (id) => `/change-order/edit/${id}`,
  };

  /**
   * Route to the ticket's linked module record in edit/create mode, using
   * moduleID/referenceID (already tracked across both modes), or null when
   * the module isn't mapped or no record is selected yet.
   */
  getRecordRoute(): string | null {
    if (!this.moduleID || !this.referenceID) {
      return null;
    }

    const buildRoute = this.LINKED_RECORD_ROUTES[this.moduleID];
    return buildRoute ? buildRoute(this.referenceID) : null;
  }

  /**
   * Get status from form
   * Returns the status name as a string
   * @returns Status name string (lowercase)
   */
  getStatusFromForm(): string {
    // First check if model has status_name (edit mode)
    if (this.model?.status_name) {
      return this.model.status_name.toLowerCase();
    }

    // Get status ID from form
    const statusId = this.ticketForm.get('status')?.value;

    // Find status object in statuses array
    const status = this.statuses.find((s: any) => s.id === statusId);

    // Return status name or default to 'new'
    return status?.name?.toLowerCase() || 'new';
  }

  getSelectedStatus(): any {
    const statusId = this.ticketForm.get('status')?.value;
    return this.statuses.find((s: any) => s.id === statusId);
  }

  getSelectedMilestone(): any {
    const milestoneId = this.ticketForm.get('milestone_id')?.value;
    return this.milestones.find((m: any) => m.id === milestoneId) || null;
  }

  getPriorityText(priority: number): string {
    const priorityMap: { [key: number]: string } = {
      1: 'Low',
      2: 'Medium',
      3: 'High',
      4: 'Urgent',
    };
    return priorityMap[priority] || 'Medium';
  }

  /**
   * Get selected scopes as full objects for display in Details Tab
   * Maps scope IDs from form control to full scope objects with {id, name, color}
   * Handles both edit mode (IDs only) and view mode (objects)
   * @returns Array of scope objects for display
   */
  getSelectedScopesForDisplay(): any[] {
    const scopeValue = this.ticketForm.get('scopes')?.value;

    // If no value, return empty array
    if (!scopeValue) {
      return [];
    }

    // If already an array of objects (view mode), return as-is
    if (Array.isArray(scopeValue) && scopeValue.length > 0 && typeof scopeValue[0] === 'object') {
      return scopeValue;
    }

    // If array of IDs (edit mode), map to full scope objects
    if (Array.isArray(scopeValue)) {
      return scopeValue
        .map((id: any) => {
          // Handle both number IDs and object IDs
          const scopeId = typeof id === 'object' && id !== null ? id.id : id;
          return this.scopes.find((s: any) => s.id === scopeId);
        })
        .filter((scope: any) => scope != null); // Remove undefined/null entries
    }

    return [];
  }

  // =============================================================================
  // SAVE & FORM DATA
  // =============================================================================

  MapDataOfArray(item: any) {
    return { id: item?.user.id, type: item?.type };
  }

  private mapPersonUser(data: any) {
    const attachable = data?.attachable || {};
    const profileImage =
      data?.profile_image ||
      attachable?.profile_image ||
      data?.profile_image_url ||
      null;

    const profileUrl =
      data?.profile_url ||
      data?.avatar_url ||
      profileImage?.url ||
      attachable?.profile_url ||
      null;

    return {
      id: data?.id,
      full_name:
        data?.full_name && data?.full_name.trim()?.length
          ? data.full_name
          : attachable?.name || '',
      avatar: data?.avatar || attachable?.avatar || null,
      profile_image: profileImage,
      profile_url: profileUrl,
    };
  }

  getFormData() {
    const form = this.ticketForm.value;
    return {
      parent_id: this.referenceID,
      module_id: form.module_id,
      reference_id: form.reference_id,
      name: form.title,
      description: form.description,
      status: form.status,
      priority: form.priority,
      responsible_person: this.responsibleUsersArray?.map((x: any) =>
        this.MapDataOfArray(x)
      ),
      participants: this.participantArray?.map((x: any) =>
        this.MapDataOfArray(x)
      ),
      observers: this.observerArray.map((x: any) => this.MapDataOfArray(x)),
      // Whichever of the above (if any) currently has the star — persisted
      // as an is_default flag on that person's ticket_associations row, and
      // read back via GET ticket/default-people the next time a NEW ticket
      // is opened. Only 'Users'-type people can be a default.
      default_responsible_person_id:
        this.responsibleUsersArray?.find((x: any) => x.isDefault && x.type === 'Users')?.user?.id ?? null,
      default_observer_id:
        this.observerArray?.find((x: any) => x.isDefault && x.type === 'Users')?.user?.id ?? null,
      default_participant_id:
        this.participantArray?.find((x: any) => x.isDefault && x.type === 'Users')?.user?.id ?? null,
      mentions: this.selectedMentionedUsers?.map((x: any) => x.id), // Send IDs only (match task module)
      is_participant: this.CONST_PARTICIPANT,
      is_observer: this.CONST_OBSERVER,
      is_time_planning: this.CONST_TIME_PLANNING,
      due_date: form?.due_date
        ? formatDate(form?.due_date, 'yyyy-MM-dd', 'en_US')
        : '',
      start_date: form?.start_date
        ? formatDate(form?.start_date, 'yyyy-MM-dd', 'en_US')
        : '',
      date_finished: form?.date_finished
        ? formatDate(form?.date_finished, 'yyyy-MM-dd', 'en_US')
        : '',
      is_created_by: this.CONST_CREATED_BY,
      created_by: form.created_by,
      ticket_type_id: form?.ticket_type_id || null,
      scopes: Array.isArray(form?.scopes)
        ? form.scopes
            .map((s: any) => (typeof s === 'object' && s !== null ? s.id : s))
            .filter((id: any) => id != null)
        : [],
      stage_id: form?.stage_id,
      milestone_id: form?.milestone_id,
      duration: form?.duration,
      check_list: Array.isArray(form?.checklists)
        ? form.checklists.map((item: any) => ({
            id: item?.id || null,
            text: item?.text || '',
            observers: item?.observers || [],
            participants: item?.participants || [],
            // Linked Materials & Services line items, by tickets_item id — or
            // job_scope_item_id for rows buffered before the ticket exists.
            items: toChecklistItemRefs(item?.items),
            is_completed: item?.is_completed || false,
            is_important: item?.is_important || false,
          }))
        : [],
      // Materials & Services chosen before the ticket existed. Rows already
      // attached (they have an id) are skipped — the server owns those.
      ticket_items: Array.isArray(form?.ticket_items)
        ? form.ticket_items
            .filter((item: any) => !item?.id && item?.job_scope_item_id)
            .map((item: any) => ({
              job_scope_item_id: item.job_scope_item_id,
              linked_scope_id: item?.linked_scope_id ?? null,
            }))
        : [],
      is_template: form?.is_template ? 1 : 0,
      reminder: 'reminder',
      vendor_id:
        typeof form?.vendor === 'object'
          ? form?.vendor?.id
          : form?.vendor || null,
      customer_id:
        typeof form?.customer === 'object'
          ? form?.customer?.id
          : form?.customer || null,
      contact_id:
        typeof form?.contact === 'object'
          ? form?.contact?.id
          : form?.contact || null,
      subcontractor_id:
        typeof form?.subcontractor === 'object'
          ? form?.subcontractor?.id
          : form?.subcontractor || null,
      party_id: form?.party_id || null,
      attachment_ids: this.attachmentsToAdd,
      tags: this.ticketTags.map((tag) => ({ tag: tag.name, color: tag.color })),
      is_favorite: this.isFavorite ? 1 : 0,
      self_assign: form?.self_assign ? 1 : 0,
      is_billable: form?.is_billable ? 1 : 0,
    };
  }

  /**
   * Create new tags as system tags before saving ticket
   * Tags without IDs are considered new and will be created via API
   *
   * Flow:
   * 1. Filter tags that don't have an ID (new tags)
   * 2. Create parallel API requests for each new tag
   * 3. Wait for all tag creation requests to complete
   * 4. Update local ticketTags array with returned IDs
   * 5. Return Observable that can be chained with save operation
   *
   * @returns Observable<any> - Completes when all tags are created
   */
  private createNewSystemTags(): Observable<any> {
    // Identify new tags (tags without IDs)
    const newTags = this.ticketTags.filter((tag) => !tag.id);

    // If no new tags to create, return immediately
    if (newTags.length === 0) {
      return of(null); // Return empty Observable
    }

    // Color name to hex code mapping for consistent color handling
    const colorHexMap: { [key: string]: string } = {
      blue: '#3b82f6',
      green: '#22c55e',
      yellow: '#eab308',
      red: '#ef4444',
      purple: '#a855f7',
      orange: '#f97316',
      gray: '#6b7280',
    };

    // Create API request for each new tag
    const createRequests = newTags.map((tag) => {
      // Build tag payload
      const payload = {
        module_id: this.TICKET_MODULE_ID, // 8 for tickets
        reference_id: 0, // 0 indicates system tag (not reference-specific)
        tag: tag.name.trim(),
        color: colorHexMap[tag.color?.toLowerCase()] || tag.color || '#6b7280',
        is_system: 1, // Mark as system tag
      };

      // Use ticketDataService.createTag() method
      return this.ticketDataService.createTag(payload).pipe(
        catchError((error) => {
          console.error(`Failed to create tag "${tag.name}":`, error);
          // Return null on error to prevent entire operation from failing
          return of(null);
        })
      );
    });

    // Execute all tag creation requests in parallel
    return forkJoin(
      createRequests.length > 0 ? createRequests : [of(null)]
    ).pipe(
      switchMap((responses: any[]) => {
        // Update ticketTags array with newly created tag IDs - Use immutable pattern
        this.ticketTags = this.ticketTags.map((tag) => {
          // Find matching response for this tag
          const responseIndex = newTags.findIndex(
            (newTag) => newTag.name === tag.name && !tag.id
          );

          if (responseIndex > -1 && responses[responseIndex]?.data?.id) {
            // Return new tag object with ID from API
            return {
              ...tag,
              id: responses[responseIndex].data.id,
            };
          }

          // Return unchanged tag
          return tag;
        });

        // Trigger change detection after updating IDs
        this.cdr.detectChanges();

        // Return empty Observable to signal completion
        return of(null);
      }),
      catchError((error) => {
        console.error('Error creating system tags:', error);
        // Don't fail the entire save operation if tag creation fails
        // User can manually add tags later
        this.toasterService.showWarning(
          'Some tags could not be created. Ticket will be saved without tags.'
        );
        return of(null);
      })
    );
  }

  /**
   * Save ticket with tag creation
   *
   * Flow:
   * 1. Validate form fields (dates, required fields)
   * 2. Create new system tags (if any)
   * 3. Save/update ticket with all tags (including newly created ones)
   * 4. Close dialog on success
   *
   * @returns boolean - True if validation passed and save initiated
   */
  save() {
    const form = this.ticketForm.value;
    let isInvalid = false;

    // Validate dates
    if (!this.validateDates()) {
      isInvalid = true;
      this.toasterService.showWarning(
        'Date',
        'Start date cannot be greater than finish date.'
      );
    }

    // Validate required fields
    if (!form.title) {
      isInvalid = true;
      this.toasterService.showWarning('Name', 'Name field is required.');
    }

    if (!form.reference_id) {
      isInvalid = true;
      this.toasterService.showWarning(
        'Reference',
        'Reference Number field is required.'
      );
    }

    if (!form.module_id) {
      isInvalid = true;
      this.toasterService.showWarning('Module', 'Module Field is Required.');
    }

    // Stop if validation failed
    if (isInvalid) {
      return true;
    }

    // Prevent multiple simultaneous save operations
    if (this.isSaving) {
      return true;
    }

    // Set loading state
    this.isSaving = true;

    // STEP 1: Create new tags first (tags without IDs)
    // STEP 2: Then save ticket with all tags (including newly created ones)
    this.createNewSystemTags()
      .pipe(
        switchMap(() => {
          // After tags are created, proceed with ticket save
          const service = this.model
            ? this.ticketDataService.update(this.model.id, this.getFormData())
            : this.ticketDataService.create(this.getFormData());

          return service;
        }),
        catchError((error) => {
          this.isSaving = false;
          this.toasterService.showError(
            error?.error?.message || 'Failed to save ticket. Please try again.'
          );
          // Return empty Observable to prevent further execution
          return of(null);
        })
      )
      .subscribe(
        (response: any) => {
          // Only process if response is not null (not an error case)
          if (response) {
            this.isSaving = false;
            const message =
              response?.data?.message ||
              response?.message ||
              'Ticket saved successfully!';
            this.toasterService.showSuccessMessage(message);
            this.close(true);
          }
        },
        (error: any) => {
          // This shouldn't be reached due to catchError above, but included for safety
          this.isSaving = false;
          this.toasterService.showError(
            error?.error?.message || 'Failed to save ticket. Please try again.'
          );
        }
      );

    return true;
  }

  // =============================================================================
  // DIALOG ACTIONS
  // =============================================================================

  onConfirmation() {
    if (!this.model?.id) {
      this.toasterService.showWarning(
        'Delete',
        'Cannot delete unsaved ticket.'
      );
      return;
    }

    const confirmDialog = confirm(
      'Are you sure you want to delete this ticket? This action cannot be undone.'
    );

    if (confirmDialog) {
      this.ticketDataService.delete(this.model.id).subscribe(
        (response: any) => {
          const message =
            response?.data?.message ||
            response?.message ||
            'Ticket deleted successfully!';
          this.toasterService.showSuccessMessage(message);
          this.close(true);
        },
        (error: any) => {
          this.toasterService.showError(
            'Delete Failed',
            error?.error?.message || 'Failed to delete ticket.'
          );
        }
      );
    }
  }

  close = (data: any = null) => {
    if (data) {
      this.scrumCommunication.setScrumBoardData(data);
    }
    this.dialogRef.close(data);
  };

  // =============================================================================
  // UTILITY METHODS
  // =============================================================================

  getChipIconUsingType(type: any) {
    let image = '';
    switch (type) {
      case this.TYPE_USER:
        image = '../../../../assets/img/general_icons/user-light.png';
        break;
      case this.TYPE_CUSTOMERS:
        image = '../../../../assets/img/general_icons/cust-light.png';
        break;
      case this.TYPE_VENDORS:
        image = '../../../../assets/img/general_icons/vendor-light.png';
        break;
      case this.TYPE_SUBCONTRACTORS:
        image = '../../../../assets/img/general_icons/subcon-light.png';
        break;
      default:
        image = '../../../../assets/img/general_icons/admin-light.png';
    }
    return image;
  }

  getChipNameUsingType(user: any, type: any) {
    let name = '';
    const cUser = user?.user;
    switch (type) {
      case this.TYPE_USER:
        name = cUser?.full_name ?? cUser?.full_name;
        break;
      case this.TYPE_CUSTOMERS:
        name = cUser?.full_name ?? cUser?.company_name;
        break;
      case this.TYPE_VENDORS:
        name = cUser?.full_name ?? cUser?.name;
        break;
      case this.TYPE_SUBCONTRACTORS:
        name = cUser?.full_name ?? cUser?.name;
        break;
      default:
        name = cUser.full_name;
    }
    return name;
  }

  /**
   * Calculate ticket progress based on status and checklist completion
   * Returns 100% if ticket is marked complete, otherwise checklist percentage
   * @returns Completion percentage (0-100)
   */
  private calculateCompletionPercentage(): number {
    // Check if ticket is marked as complete via status
    const status = this.getStatusFromForm();
    if (this.isTicketComplete(status)) {
      return 100;
    }

    // Calculate based on checklist completion
    const checklist = this.ticketForm.get('checklists')?.value || [];

    // Edge case: No checklist items
    if (!Array.isArray(checklist) || checklist.length === 0) {
      return 0;
    }

    // Count completed checklist items
    const completed = checklist.filter((item: any) =>
      this.isChecklistItemCompleted(item)
    ).length;

    // Calculate percentage and round to nearest integer
    return Math.round((completed / checklist.length) * 100);
  }

  /**
   * Check if a status indicates ticket completion
   * @param status - Status string from form
   * @returns True if status represents completion state
   */
  private isTicketComplete(status: string): boolean {
    if (!status) return false;

    // Check by status name (case-insensitive)
    const statusName = status.toLowerCase().trim();

    // Common completion status keywords
    const completionKeywords = [
      'complete',
      'completed',
      'done',
      'finished',
      'closed',
      'resolved',
      'solved',
    ];

    return completionKeywords.some((keyword) => statusName.includes(keyword));
  }

  private isChecklistItemCompleted(item: any): boolean {
    const value =
      item?.is_completed ?? item?.completed ?? item?.status ?? false;

    if (typeof value === 'string') {
      return value === '1' || value.toLowerCase() === 'true';
    }

    return value === true || value === 1;
  }
}
